import { isStandardRole, useUserRoleForCurrentWorkspace } from '@/features/roles/hook';
import { authStore } from '@/services/auth/store';
import { getMasqBarVisibility, setMasqBarVisibility } from '@/utils/masq';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { Role } from '@customerio/hydra-permissions/roles';
import {
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  Icon,
  Text,
  Toggle,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useState } from 'react';
import { IconButton } from '../icon-button';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { getWorkspaceSettingsLink } from '@/hooks/get-workspace-settings-link';
import { useSampleWorkspace } from '@/features/workspaces/sample-workspace';
import useSetupGuideExperiment from '@/features/experiments/setup-guide/hook';

function currentUserRoleString(role: string) {
  const roleStrings: Record<Role, string> = {
    [Role.Admin]: 'an Account admin',
    [Role.Manager]: 'a Workspace admin',
    [Role.Author]: 'an Author',
    [Role.AuthorRedacted]: 'an Author',
    [Role.AuthorRedactedSensitive]: 'an Author',
    [Role.AuthorRedactedAll]: 'an Author',
    [Role.Viewer]: 'a Viewer',
    [Role.ViewerRedacted]: 'a Viewer',
    [Role.ViewerRedactedSensitive]: 'a Viewer',
    [Role.ViewerRedactedAll]: 'a Viewer',
  };

  if (isStandardRole(role)) {
    return `You're ${roleStrings[role]}`;
  }

  return role;
}

export function PersonDropdown() {
  const auth = useStore(authStore);
  const { hasPermission } = usePermissions();
  const userRole = useUserRoleForCurrentWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const { isSampleWorkspace } = useSampleWorkspace();
  const workspaceSettingsLink = getWorkspaceSettingsLink();
  const { isLoading: isSetupGuideLoading, setupGuide } = useSetupGuideExperiment();

  const showAdminMasqueradeOption =
    ((auth.account?.admin ?? false) && (auth.user?.can_masquerade ?? false)) || (auth.account?.is_masq ?? false);

  const isSetupGuideIncomplete = !isSetupGuideLoading && setupGuide.completed_at == null;

  const showSetUpWorkspaceNavItem =
    isSetupGuideIncomplete &&
    window.sessionStorage.getItem('showSetUpWorkspaceNavItem') === 'true' &&
    !isSampleWorkspace(currentWorkspace?.id);

  const [isShowingMasqBar, setIsShowingMasqBar] = useState(getMasqBarVisibility);

  const onMasqueradeChange = (event: React.FormEvent<HTMLInputElement>) => {
    event.preventDefault();

    const newValue = !isShowingMasqBar;
    setMasqBarVisibility(newValue);
    setIsShowingMasqBar(newValue);

    // If turning off masq bar, remove masq_account_id from URL
    if (!newValue) {
      const url = new URL(window.location.href);
      url.searchParams.delete('masq_account_id');
      window.location.href = url.toString();
    } else {
      // Reload the page, so that the Journeys UI picks up the local storage update
      window.location.reload();
    }
  };

  return (
    <>
      <DropdownMenu>
        {(context) => (
          <IconButton
            label="Menu"
            icon="manage"
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          />
        )}
        <DropdownMenuContent>
          {userRole != null || showSetUpWorkspaceNavItem ? (
            <DropdownMenuGroup>
              {userRole != null ? (
                <DropdownMenuItem>
                  {currentUserRoleString(userRole.name)}
                  {userRole.redacted ? (
                    <div style={{ width: '20px', height: '20px', marginLeft: '8px' }}>
                      <Icon name="view-off" size="fill" display="block" />
                    </div>
                  ) : null}
                </DropdownMenuItem>
              ) : null}
              {showSetUpWorkspaceNavItem ? (
                <DropdownMenuItem href={`/workspaces/${currentWorkspace?.id}/journeys/home/workspace-setup`}>
                  Set up workspace
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
          ) : null}

          <DropdownMenuGroup>
            <DropdownMenuItem href="/settings/personal">Personal settings</DropdownMenuItem>
            <DropdownMenuItem href="/settings/experimental-features">Experimental features</DropdownMenuItem>
          </DropdownMenuGroup>
          <DropdownMenuGroup>
            <DropdownMenuItem href="/settings">Account settings</DropdownMenuItem>
            <DropdownMenuItem href="/settings/privacy">Privacy, Data &amp; AI</DropdownMenuItem>
            {hasPermission('account/billing/view') === true ? (
              <DropdownMenuItem href="/settings/billing/your-plan">Plans &amp; Billing</DropdownMenuItem>
            ) : null}
            <DropdownMenuItem href={workspaceSettingsLink(currentWorkspace?.id ?? 'last')}>
              Workspace settings
            </DropdownMenuItem>
          </DropdownMenuGroup>
          {showAdminMasqueradeOption ? (
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Box display="flex" alignItems="center">
                  <Text
                    mr="400"
                    display="inline"
                    as="label"
                    id="toggle-admin-masq-label"
                    htmlFor="toggle-admin-masq"
                    textTransform="capitalize"
                  >
                    Admin masquerade
                  </Text>
                  <Toggle
                    size="sm"
                    labelOn="On"
                    labelOff="Off"
                    onChange={(event) => onMasqueradeChange(event)}
                    ariaLabelledby="toggle-admin-masq-label"
                    id="toggle-admin-masq"
                    isChecked={isShowingMasqBar}
                  />
                </Box>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          ) : null}
          <DropdownMenuGroup>
            <DropdownMenuItem href="/logout" isDanger={true}>
              Log out
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
